import React, { useState, useEffect, useMemo } from "react";
import {
  NotificationState,
  Email as EmailType,
  Status as StatusType,
  Configuration as ConfigurationType,
  Rule,
  RulesSpec,
  HysteresisMode,
} from "../../../features/notifications/notificationSlice";
import NotificationName from "./NotificationName";
import NotificationConfiguration from "./NotificationConfiguration";
import NotificationEmail from "./NotificationEmail/NotificationEmail";
import { useParams } from "react-router-dom";
import { useGetGroupQuery } from "../../../services/groupAPI";
import { toast } from "react-toastify";
import useGetLanguage from "../../../utils/useGetLanguage";
import { LanguageText } from "../../../dictionary/GroupNotificationText";
import NotificationStatus from "./NotificationStatus";
import NotificationRules from "./NotificationRule/NotificationRules";
import {
  useCreateNotificationMutation,
  useUpdateNotificationMutation,
} from "../../../services/groupNotificationAPI";
import {
  defaultEmail,
  defaultStatus,
  defaultConfiguration,
  ErrorType,
  convertRulesArrayToMapObject,
  convertMapObjectToArray,
  getDefaultRule,
  ruleSpecKeys,
  switchOperators,
  isValidateRule,
} from "./utils";
import { useAppSelector } from "../../../app/hooks";
import { makeSelectDevicesByGroupId } from "../../../features/devices/deviceSlice";
import isEqual from "lodash/isEqual";
import NotificationDashboard from "./NotificationDashboard";
import { useNavigate } from "react-router-dom";

export type GroupNotificationProps = {
  isNew: boolean;
  selectedNotification?: NotificationState | null;
};
const GroupNotification = ({
  isNew,
  selectedNotification,
}: GroupNotificationProps): JSX.Element => {
  const Language: LanguageText = useGetLanguage({
    fileName: "GroupNotificationText",
  });
  const { groupid } = useParams<{
    groupid: string;
  }>();
  const navigate = useNavigate();

  const selectDevicesByGroupId = useMemo(makeSelectDevicesByGroupId, []);
  const groupDevices = useAppSelector((state) => selectDevicesByGroupId(state, groupid));

  // APIs
  const {
    data: group,
    isLoading: isGroupLoading,
    error: groupError,
  } = useGetGroupQuery({ id: String(groupid) });

  const groupMembers = convertMapObjectToArray(group?.users) || [];

  const [
    create,
    {
      isLoading: isCreateLoading,
      isError: isCreateError,
      error: createError,
      data: newNotification,
      isSuccess: isCreateSuccess,
    },
  ] = useCreateNotificationMutation();

  const [
    update,
    {
      isLoading: isUpdateLoading,
      isError: isUpdateError,
      error: updateNotificationError,
      data: updatedNotification,
      isSuccess: isUpdateSuccess,
    },
  ] = useUpdateNotificationMutation();

  const [name, setName] = useState<NotificationState["name"]>(
    selectedNotification?.name || ""
  );

  const [rules, setRules] = useState<Rule[]>(
    convertMapObjectToArray(selectedNotification?.rules) || [getDefaultRule()]
  );

  const [dashboard, setDashboard] = useState<NotificationState["dashboard"]>(
    selectedNotification?.dashboard || ""
  );

  const [users, setUsers] = useState<NotificationState["users"]>(
    selectedNotification?.users || []
  );

  const [email, setEmail] = useState<EmailType>(
    selectedNotification?.email || defaultEmail
  );

  const [status, setStatus] = useState<StatusType>(
    selectedNotification?.status || defaultStatus
  );

  const [configuration, setConfiguration] = useState<ConfigurationType>(
    selectedNotification?.configuration || defaultConfiguration
  );

  const [error, setError] = useState<ErrorType>({
    name: false,
    users: false,
    email: {
      subject: false,
      body: false,
    },
    rules: {},
  });

  const addUser = (userID: string) => {
    // Is it a group member?
    if (!groupMembers?.some((member: any) => member.id === userID)) {
      toast.error(Language?.noMemberFound);
      return;
    }
    // Is already added?
    if (users?.includes(userID)) return;

    // 10 Users Max
    if (users?.length && users?.length >= 10) {
      toast.error("The maximum 10 recipients is reached");
      return;
    }

    if (userID) setUsers((prev) => [...prev, userID]);
  };

  const removeUser = (userID: string) => {
    // Check if member with the id exists in the group members
    // if (!groupMembers?.some((member: any) => member.id === userID)) {
    //   toast.error(Language?.noMemberFound);
    //   return;
    // }
    setUsers((prev) => [...prev.filter((userid: any) => userid !== userID)]);
  };

  const handleRuleSpecChange = (
    ruleID: string,
    ruleSpecKey: string,
    ruleSpecValue: any
  ) => {
    setRules((prevRules) =>
      prevRules.map((prevRule) => {
        if (prevRule.id !== ruleID) return prevRule;

        // Reset rule to default on Device change
        if (ruleSpecKey === ruleSpecKeys.device_eui) {
          const { id, spec } = getDefaultRule();
          return {
            ...prevRule,
            spec: {
              ...spec,
              device_eui: ruleSpecValue,
            },
          };
        }
        // Set the hysteresis.value to 0 when hysteresis.mode is 0(NONE)
        if (
          ruleSpecKey === ruleSpecKeys.hysteresisMode &&
          ruleSpecValue === HysteresisMode.NONE
        ) {
          return {
            ...prevRule,
            spec: {
              ...prevRule.spec,
              hysteresis: {
                mode: ruleSpecValue,
                value: 0,
              },
            },
          };
        }

        // For nested spec object
        if (ruleSpecKey.includes(".")) {
          const [outer, inner] = ruleSpecKey.split(".");
          const outerObj = prevRule.spec?.[outer as keyof RulesSpec] || {};
          return {
            ...prevRule,
            spec: {
              ...prevRule.spec,
              [outer]: {
                ...outerObj,
                [inner]: ruleSpecValue,
              },
            },
          };
        } else {
          return {
            ...prevRule,
            spec: {
              ...prevRule.spec,
              [ruleSpecKey]: ruleSpecValue,
            },
          };
        }
      })
    );
  };

  const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    const newError: ErrorType = {
      name: !name.trim(),
      users: !users.length,
      email: {
        subject: !email.subject?.trim(),
        body: !email.body?.trim(),
      },
      rules: rules.reduce((acc, cur) => {
        if (cur.id) {
          acc[cur.id!] = !isValidateRule(cur);
        }
        return acc;
      }, {} as Record<string, boolean>),
    };
    setError(newError);

    const hasError =
      newError.name ||
      newError.users ||
      newError.email.subject ||
      newError.email.body ||
      Object.values(newError.rules).some(Boolean);

    if (hasError) {
      toast.error(Language?.requiredFields);
      return;
    }
    // Remove threshold and hysteresis from rules with mag device
    const filteredRules = rules.map((rule) => {
      const op = rule.spec?.operator;

      if (typeof op === "number" && switchOperators.includes(op)) {
        const { threshold, hysteresis, ...magSpec } = rule.spec || {};
        return {
          ...rule,
          spec: magSpec,
        };
      }
      return rule;
    });

    const convertedRules = convertRulesArrayToMapObject(filteredRules);
    try {
      if (isNew) {
        const newNotification: NotificationState = {
          group_id: groupid || "",
          name,
          rules: convertedRules,
          dashboard,
          users,
          email,
          status,
          configuration,
        };
        newNotification &&
          (await create({ notification: newNotification, language: Language }));
      } else if (!isNew && selectedNotification) {
        const updatedNotification: NotificationState = {
          ...selectedNotification,
          name,
          rules: convertedRules,
          dashboard,
          users,
          email,
          status,
          configuration,
        };
        updatedNotification &&
          (await update({ notification: updatedNotification, language: Language }));
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again later");
    }
  };

  const handleAddRule = (rule: Rule) => {
    setRules((prevRules) => [...prevRules, rule]);
  };
  const handleRemoveRule = (ruleID: string) => {
    if (rules.length === 1) {
      toast.error(Language?.minRulesMsg);
      return;
    }
    if (!ruleID) return;
    setRules((prevRules) => prevRules.filter((rule) => rule.id !== ruleID));
  };

  useEffect(() => {
    if (selectedNotification) {
      setName(selectedNotification.name || "");
      setRules(
        (convertMapObjectToArray(selectedNotification?.rules) as Rule[]) || [
          getDefaultRule(),
        ]
      );
      setEmail(selectedNotification?.email || defaultEmail);
      setUsers(selectedNotification?.users || []);
      setStatus(selectedNotification?.status || defaultStatus);
      setConfiguration(selectedNotification?.configuration || defaultConfiguration);
      setDashboard(selectedNotification?.dashboard || "");
    }
  }, [selectedNotification]);

  // Resets existing rules to default when device is not found
  useEffect(() => {
    if (!selectedNotification) return;
    const updatedRules = rules.map((rule) => {
      const deviceExists = groupDevices.some((d) => d.eui === rule?.spec?.device_eui);
      if (!deviceExists) {
        const { spec } = getDefaultRule();
        return { ...rule, spec: { ...spec } };
      }
      return rule;
    });
    // Only update state if rules actually changed to avoid rerenders
    if (!isEqual(rules, updatedRules)) {
      setRules(updatedRules);
    }
  }, [groupDevices, selectedNotification, rules]);

  // Remove unknown dashboard
  useEffect(() => {
    if (!selectedNotification?.dashboard || !group?.dashboards) return;

    const isDashboard = Object.keys(group.dashboards).includes(
      selectedNotification.dashboard
    );

    if (!isDashboard) setDashboard("");
  }, [selectedNotification, group]);

  // Remove recipients no longer in the group
  useEffect(() => {
    if (!selectedNotification) return;
    selectedNotification?.users?.forEach((userId) => {
      const isMember = groupMembers.some((member) => member.user_id === userId);
      !isMember &&
        setUsers((prev) => [...prev.filter((userid: any) => userid !== userId)]);
    });
  }, [selectedNotification]);

  // Navigate to index
  useEffect(() => {
    (isCreateSuccess || isUpdateSuccess || isCreateError || isUpdateError) &&
      navigate(`/group/${groupid}/notifications`);
  }, [isCreateSuccess, isUpdateSuccess, isCreateError, isUpdateError, groupid, navigate]);

  return (
    <div className="bg-gray-100 p-6 sm:p-8 lg:p-10 w-full min-h-screen">
      <div className="mb-6 flex justify-start">
        <button
          onClick={() => navigate(`/group/${groupid}/notifications`)}
          className="inline-flex items-center px-3 py-1 sm:px-4 sm:py-2 bg-gray-300 hover:bg-gray-400 rounded-md shadow-sm text-xs sm:text-sm text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-gray-500 transition"
        >
          <span className="mr-2">&#60;</span> {Language?.back}
        </button>
      </div>

      <div className="bg-white p-6 sm:p-8 rounded-lg shadow-md w-full max-w-5xl mx-auto">
        <NotificationName name={name} onChange={setName} error={error.name} />
        <NotificationRules
          rules={rules}
          onRuleSpecChange={handleRuleSpecChange}
          onAddRule={handleAddRule}
          onRemoveRule={handleRemoveRule}
          rulesError={error?.rules}
        />
        <NotificationDashboard
          dashboard={dashboard || ""}
          onDashboardChange={setDashboard}
        />
        <NotificationEmail
          isNew={isNew}
          groupID={groupid || ""}
          groupMembers={groupMembers}
          email={email}
          onEmailChange={setEmail}
          emailError={error.email}
          users={users}
          onAddUser={addUser}
          onRemoveUser={removeUser}
          usersError={error.users}
        />
        <NotificationStatus status={status} onStatusChange={setStatus} />
        <NotificationConfiguration
          configuration={configuration}
          onConfigurationChange={setConfiguration}
        />
        <div className="flex flex-wrap justify-start mt-4 gap-2">
          <button
            disabled={isCreateLoading}
            className="w-full sm:w-auto bg-blue-500 hover:bg-blue-600 text-white font-semibold py-1 px-3 sm:py-2 sm:px-4 text-xs sm:text-sm rounded-md shadow-md transition transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-400"
            onClick={handleSubmit}
          >
            {isNew ? Language?.create : Language?.update}
          </button>
          <button
            disabled={isCreateLoading || isUpdateLoading}
            className="w-full sm:w-auto bg-gray-300 hover:bg-gray-500 text-gray-800 font-semibold py-1 px-3 sm:py-2 sm:px-4 text-xs sm:text-sm rounded-md shadow-md transition transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-gray-300"
            onClick={() => navigate(`/group/${groupid}/notifications`)}
          >
            {Language?.cancel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GroupNotification;
